import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, switchMap } from 'rxjs/operators';

import { AdminPanelService } from '@app/services/admin-panel.service';
import { CompaniesService } from '@app/services/companies.service';
import { ContractorsService } from '@app/services/contractors.service';
import { UserService } from '@app/services/user.service';
import {
  addAddressToCompany,
  addAddressToCompanyFailed,
  addAddressToCompanySuccess,
  addCompany,
  addCompanyFailed,
  addCompanySuccess,
  addCompanyWithOwner,
  addCompanyWithOwnerFailure,
  addCompanyWithOwnerSuccess,
  addEmployeeToCompany,
  addEmployeeToCompanyFailed,
  addEmployeeToCompanySuccess,
  deleteCompany,
  deleteCompanyFailed,
  deleteCompanySuccess,
  deleteEmployeeFromCompany,
  deleteEmployeeFromCompanyFailed,
  deleteEmployeeFromCompanySuccess,
  getCompaniesList,
  getCompaniesListFailed,
  getCompaniesListSuccess,
  getCompany,
  getCompanyFailed,
  getCompanySuccess,
  getContractorList,
  getContractorListFailed,
  getContractorListSuccess,
  updateAddressInCompany,
  updateAddressInCompanyFailed,
  updateAddressInCompanySuccess,
  updateCompany,
  updateCompanyFailed,
  updateCompanySuccess,
} from '@companies/actions/companies.actions';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

@Injectable()
export class CompaniesEffects {
  constructor(
    private actions$: Actions,
    private snackbarService: SnackbarService,
    private userService: UserService,
    private adminService: AdminPanelService,
    private companiesService: CompaniesService,
    private contractorsService: ContractorsService
  ) {}

  getCompaniesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCompaniesList),
      exhaustMap(action =>
        this.companiesService.getCompanyList().pipe(
          map(list => getCompaniesListSuccess({ companyList: list || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingCompanyList);
            return of(getCompaniesListFailed());
          })
        )
      )
    )
  );

  getCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCompany),
      switchMap(action =>
        this.companiesService.getCompany(action.companyId).pipe(
          map(companyModel => getCompanySuccess({ companyModel })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingCompanyData);
            return of(getCompanyFailed());
          })
        )
      )
    )
  );

  addCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCompany),
      mergeMap(action =>
        this.companiesService.addNewCompany(action.companyModel).pipe(
          mergeMap(companyModel => [addCompanySuccess({ companyModel })]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.AddingNewVendor);
            return of(addCompanyFailed());
          })
        )
      )
    )
  );

  addCompanyWithOwner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCompanyWithOwner),
      mergeMap(action =>
        this.userService.inviteUser(action.user).pipe(
          mergeMap(user =>
            this.companiesService
              .addNewCompany({
                ...action.companyModel,
                contactPersonId: user.id,
              })
              .pipe(
                mergeMap(company =>
                  this.companiesService
                    .addEmployeeToCompany({ companyId: company.id, userIds: [user.id] })
                    .pipe(map(() => company))
                ),
                mergeMap(company =>
                  this.adminService
                    .setUserToGeneralRole([{ roleId: action.roleId, userId: user.id }])
                    .pipe(map(() => company))
                )
              )
          )
        )
      ),
      map(company => addCompanyWithOwnerSuccess({ company })),
      catchError(() => {
        this.snackbarService.error(SnackbarErrorMessage.AddingNewVendor);
        return of(addCompanyWithOwnerFailure());
      })
    )
  );

  updateCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCompany),
      mergeMap(action =>
        this.companiesService.updateCompany(action.companyModel).pipe(
          mergeMap(companyModel => [updateCompanySuccess({ companyModel })]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingVendor);
            return of(updateCompanyFailed());
          })
        )
      )
    )
  );

  deleteCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCompany),
      mergeMap(action =>
        this.companiesService.deleteCompany(action.companyId).pipe(
          mergeMap(() => [deleteCompanySuccess(), getCompaniesList()]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingVendor);
            return of(deleteCompanyFailed());
          })
        )
      )
    )
  );

  addEmployeeToCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addEmployeeToCompany),
      mergeMap(action =>
        this.companiesService.addEmployeeToCompany(action.updateEmployeeItem).pipe(
          mergeMap(companyList => [
            addEmployeeToCompanySuccess(),
            getCompany({ companyId: action.updateEmployeeItem.companyId }),
            getContractorList(),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.AddingEmployee);
            return of(addEmployeeToCompanyFailed());
          })
        )
      )
    )
  );

  deleteEmployeeFromCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteEmployeeFromCompany),
      mergeMap(action =>
        this.companiesService.deleteEmployeeFromCompany(action.updateEmployeeItem).pipe(
          mergeMap(companyList => [
            deleteEmployeeFromCompanySuccess(),
            getCompany({ companyId: action.updateEmployeeItem.companyId }),
            getContractorList(),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingEmployee);
            return of(deleteEmployeeFromCompanyFailed());
          })
        )
      )
    )
  );

  getContractorList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getContractorList),
      exhaustMap(() => {
        return this.contractorsService.getUnassignedContractorsList().pipe(
          map(contractors => getContractorListSuccess({ contractors })),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingVendors);
            return of(getContractorListFailed());
          })
        );
      })
    )
  );

  addAddressToCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addAddressToCompany),
      mergeMap(action => {
        return this.companiesService.addAddressToCompany(action.postCompanyAddressModel).pipe(
          mergeMap(contractors => [
            addAddressToCompanySuccess(),
            getCompany({ companyId: action.postCompanyAddressModel.companyId }),
          ]),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.AddingAddress);
            return of(addAddressToCompanyFailed());
          })
        );
      })
    )
  );

  updateAddressInCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateAddressInCompany),
      mergeMap(action => {
        return this.companiesService.updateAddressInCompany(action.restAddressModel, action.companyId).pipe(
          mergeMap(contractors => [updateAddressInCompanySuccess(), getCompany({ companyId: action.companyId })]),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingAddress);
            return of(updateAddressInCompanyFailed());
          })
        );
      })
    )
  );
}
